/**
 * Recall for the composer — Up walks back through what you have sent, shell
 * style.
 *
 * The list is the conversation's own user messages (already loaded to render
 * the transcript, so reopening a thread or reloading the page keeps recall),
 * messages still queued behind a running reply, and sends that never made it
 * into the transcript: a failed upload, or a turn the server refused. Failed
 * sends have no row anywhere else, so they are kept
 * here — in a module-level cache rather than storage, because chat text is the
 * thing on this surface most likely to carry patient detail. Keeping it in
 * memory means it dies with the tab and never lands on disk.
 *
 * Kept per conversation. A thread is the unit the reader is actually working
 * in — recalling something typed into a different one is a surprise, not a
 * convenience.
 */
import type { ChatMessage } from "~/pages/appointments/hooks/useAgentChat";

interface FailedSend {
  text: string;
  at: Date;
}

/** Entries Up walks through, at most. */
export const RECALL_LIMIT = 100;

/** Failed sends kept per conversation. */
const MAX_FAILED = RECALL_LIMIT;

/**
 * Conversations kept at all. Switching threads is cheap and common — a
 * regenerated review starts a new one each time — so the map would otherwise
 * grow for as long as the tab is open.
 */
const MAX_CONVERSATIONS = 20;

/** Conversation id → its failed sends, oldest first. Insertion order is recency. */
let failedByConversation = new Map<string, FailedSend[]>();

/** Record a send that never reached the transcript. No-ops on blanks. */
export function rememberFailedSend(
  conversationId: string,
  text: string,
  at: Date,
): void {
  const trimmed = text.trim();
  if (!conversationId || !trimmed) return;
  const existing = failedByConversation.get(conversationId) ?? [];
  // Re-inserting moves this conversation to the end, so the map stays ordered
  // by how recently it was used and the oldest is the one to drop.
  failedByConversation.delete(conversationId);
  failedByConversation.set(
    conversationId,
    [...existing, { text: trimmed, at }].slice(-MAX_FAILED),
  );
  while (failedByConversation.size > MAX_CONVERSATIONS) {
    const oldest = failedByConversation.keys().next().value;
    if (oldest === undefined) break;
    failedByConversation.delete(oldest);
  }
}

export function failedSends(conversationId: string): readonly FailedSend[] {
  return failedByConversation.get(conversationId) ?? [];
}

/**
 * The recall list, newest first, at most `RECALL_LIMIT`: queued messages
 * (typed after everything else, so newest), then the transcript's user
 * messages and the failed sends merged by time. Blanks are dropped and
 * consecutive repeats collapsed (a refused send retried verbatim is one entry,
 * not two).
 */
export function recallEntries(
  messages: readonly ChatMessage[],
  failed: readonly FailedSend[],
  queued: readonly string[],
): string[] {
  const merged: FailedSend[] = [
    ...messages
      .filter((m) => m.role === "user")
      .map((m) => ({ text: m.content.trim(), at: m.timestamp })),
    ...failed,
  ];
  // Stable, so same-instant entries keep transcript order.
  merged.sort((a, b) => a.at.getTime() - b.at.getTime());
  const oldestFirst = [...merged.map((e) => e.text), ...queued];
  const entries: string[] = [];
  for (let i = oldestFirst.length - 1; i >= 0; i--) {
    if (entries.length === RECALL_LIMIT) break;
    const text = oldestFirst[i].trim();
    if (text && text !== entries.at(-1)) entries.push(text);
  }
  return entries;
}

/** Test seam — the cache is module state and would otherwise leak between tests. */
export function resetComposerHistory(): void {
  failedByConversation = new Map();
}
